// src/pages/AddProperty.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Button,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

export default function AddProperty() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    type: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    owners: [],
    bankAccount: '',
    depositAccount: '',
    reserve: '',
    manager: ''
  });

  const [bankOptions, setBankOptions] = useState([]);
  const [adminOptions, setAdminOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const banksSnap = await getDocs(collection(db, 'bankAccounts'));
      const banks = banksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBankOptions(banks);

      const q = query(collection(db, 'users'), where('role', '==', 'Admin'));
      const adminsSnap = await getDocs(q);
      const admins = adminsSnap.docs.map(doc => ({
        uid: doc.id,
        name: doc.data().name || 'Unnamed',
        email: doc.data().email || ''
      }));
      setAdminOptions(admins);
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOwnersChange = (event) => {
    const selected = event.target.value;
    const selectedObjects = adminOptions.filter(admin =>
      selected.includes(admin.uid)
    );
    setFormData(prev => ({ ...prev, owners: selectedObjects }));
  };

  const handleSubmit = async () => {
    await addDoc(collection(db, 'properties'), {
      ...formData,
      createdAt: serverTimestamp()
    });
    navigate('/pm/properties');
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Add Property</Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>New Property</Typography>

        <TextField
          label="Property Type"
          select
          fullWidth
          name="type"
          value={formData.type}
          onChange={handleChange}
          sx={{ mb: 2 }}
        >
          <MenuItem value="Single Family">Single Family</MenuItem>
          <MenuItem value="Multi Family">Multi Family</MenuItem>
          <MenuItem value="Condo">Condo</MenuItem>
        </TextField>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">What is the street address?</Typography>

        <TextField label="Street Address" fullWidth name="street" value={formData.street} onChange={handleChange} sx={{ mt: 1, mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="City" name="city" value={formData.city} onChange={handleChange} fullWidth />
          <TextField label="State" name="state" value={formData.state} onChange={handleChange} fullWidth />
          <TextField label="ZIP" name="zip" value={formData.zip} onChange={handleChange} fullWidth />
        </Box>

        <TextField label="Country" fullWidth name="country" value={formData.country} onChange={handleChange} sx={{ mt: 2 }} />

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Who is the property owner?</Typography>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Owners</InputLabel>
          <Select
            multiple
            value={formData.owners.map(o => o.uid)}
            onChange={handleOwnersChange}
            input={<OutlinedInput label="Owners" />}
            renderValue={(selected) =>
              selected.map(id => {
                const owner = adminOptions.find(o => o.uid === id);
                return owner ? owner.name : id;
              }).join(', ')
            }
          >
            {adminOptions.map(admin => (
              <MenuItem key={admin.uid} value={admin.uid}>
                <Checkbox checked={formData.owners.some(o => o.uid === admin.uid)} />
                <ListItemText primary={admin.name} secondary={admin.email} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">What are this property's primary bank accounts?</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Operating Account</InputLabel>
            <Select
              name="bankAccount"
              value={formData.bankAccount}
              onChange={handleChange}
              label="Operating Account"
            >
              {bankOptions.map(bank => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} • ****{bank.last4}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Deposit Trust Account</InputLabel>
            <Select
              name="depositAccount"
              value={formData.depositAccount}
              onChange={handleChange}
              label="Deposit Trust Account"
            >
              <MenuItem value="">None</MenuItem>
              {bankOptions.map(bank => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name} • ****{bank.last4}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField label="Property Reserve" fullWidth name="reserve" value={formData.reserve} onChange={handleChange} sx={{ mt: 2 }} />

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Who will be the primary manager of this property?</Typography>
        <TextField label="Manager (optional)" fullWidth name="manager" value={formData.manager} onChange={handleChange} sx={{ mt: 1 }} />

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="contained" onClick={handleSubmit}>Create Property</Button>
          <Button variant="text" onClick={() => navigate(-1)}>Cancel</Button>
        </Box>
      </Paper>
    </Box>
  );
}